import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import { ErrorResponse } from "../datasources/api";
import { sendGet } from "../datasources/networking/resources";
import { PaginationPayload, ResourceUri } from "../datasources/requests/common";
import { useCurrentLanguage } from "../i18n";
import { invalidateCache, reloadFn } from "./common";
import { useAuthentication } from "./useAuthentication";


/**
 * Hook useResources returns all functions and data for displaying and fetching lists of data.
 * Generic parameter represents the response type and **must** be either a PaginatedResponse or a NonPaginatedResponse.
 *
 * @param uri the resource URI
 * @param usePagination if true, the URI will be enriched with pagination options.
 */
export const useResources = <Res>(uri: ResourceUri, usePagination?: boolean) => {
	const { forceSignIn } = useAuthentication();
	const client = useQueryClient();
	const language = useCurrentLanguage();
	const [pageSizes, setPageSizes] = useState<Array<number>>([10, 25, 50]);
	const [pagination, setPagination] = useState<PaginationPayload>({ page: 0, pageSize: 10 });
	const paginationKey = usePagination ? "paginated" : "non-paginated";
	const _uri = usePagination ? { ...uri, query: { ...uri.query, ...pagination } } : uri;

	const reload = () => reloadFn(client, [uri]);
	const clearCache = () => invalidateCache(client, [uri]);

	const { data, isLoading, isFetching, error } = useQuery<Res, ErrorResponse>({
		queryKey: [uri, "get", paginationKey, pagination, language],
		queryFn: async () => {
			const response = await sendGet<Res>(_uri);

			const { pageSizes } = response as any;
			if (Array.isArray(pageSizes)) {
				setPageSizes(pageSizes);
			}

			return response;
		},
	});

	if (error && error.httpCode === HttpStatusCode.Unauthorized) forceSignIn(error);
	return {
		data,
		isLoading: isLoading || isFetching,
		error,
		pagination,
		pageSizes,
		setPagination,
		reload,
		clearCache,
	};
};
